import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import type { HmiSymbolDefinition, HmiSymbolLayer } from "../schema/hmi-symbol.schema";

const HMI_DEFAULT_LINE_COLOR = "#1f6f54";

function nextSymbolId(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return `custom.${slug || "symbol"}`;
}

export function canCreateCustomSymbol(element: HmiElement | null): boolean {
  return Boolean(
    element &&
      (element.type === "rectangle" ||
        element.type === "circle" ||
        element.type === "line" ||
        element.type === "text"),
  );
}

function createLayersFromElement(element: HmiElement): HmiSymbolLayer[] {
  if (element.type === "rectangle") {
    return [
      {
        id: `${element.id}_rect`,
        type: "rect",
        style: {
          x: 0,
          y: 0,
          width: element.width,
          height: element.height,
          rx: element.cornerRadius,
          fill: "#f9fbfa",
          stroke: "#2c5a49",
          strokeWidth: 3,
        },
        bindings: [],
      },
    ];
  }
  if (element.type === "circle") {
    return [
      {
        id: `${element.id}_circle`,
        type: "circle",
        style: {
          cx: element.width / 2,
          cy: element.height / 2,
          r: Math.min(element.width, element.height) / 2 - 2,
          fill: "#dceae2",
          stroke: "#2c5a49",
          strokeWidth: 3,
        },
        bindings: [],
      },
    ];
  }
  if (element.type === "line") {
    const [x1, y1, x2, y2] = element.points;
    const stroke = (element.style as Record<string, unknown> | undefined)?.stroke;
    return [
      {
        id: `${element.id}_line`,
        type: "line",
        style: {
          x1,
          y1,
          x2,
          y2,
          stroke: typeof stroke === "string" && stroke.trim() ? stroke.trim() : HMI_DEFAULT_LINE_COLOR,
          strokeWidth: 4,
          strokeLinecap: "round",
        },
        bindings: [],
      },
    ];
  }
  if (element.type === "text") {
    return [
      {
        id: `${element.id}_text`,
        type: "text",
        style: {
          x: 0,
          y: Math.min(element.height - 8, 28),
          text: element.text,
          fill: "#0f172a",
          fontSize: 20,
          fontWeight: 600,
        },
        bindings: [],
      },
    ];
  }
  return [];
}

export function saveElementAsCustomSymbol(
  document: HmiCanvasDocument,
  elementId: string,
  options: {
    title: string;
    category: HmiSymbolDefinition["category"];
    tenantId: string;
    existingSymbolId?: string | null;
  },
): HmiCanvasDocument {
  const element = document.elements.find((entry) => entry.id === elementId);
  if (!canCreateCustomSymbol(element ?? null) || !element) {
    return document;
  }
  const existing =
    options.existingSymbolId
      ? document.symbols.find((symbol) => symbol.id === options.existingSymbolId && !symbol.builtIn)
      : undefined;
  const definitionId = existing?.id ?? nextSymbolId(options.title);
  const nextVersion = existing ? existing.version + 1 : 1;
  const definition: HmiSymbolDefinition = {
    id: definitionId,
    version: nextVersion,
    title: options.title,
    category: options.category,
    builtIn: false,
    viewport: {
      width: element.width,
      height: element.height,
    },
    layers: createLayersFromElement(element),
    metadata: {
      tenantId: options.tenantId,
      description: `Derived from ${element.name}`,
    },
  };
  const nextSymbols = existing
    ? document.symbols.map((symbol) => (symbol.id === existing.id ? definition : symbol))
    : [...document.symbols, definition];
  const symbolElement = {
    ...element,
    type: "symbol_instance",
    symbol: {
      definitionId,
      definitionVersion: nextVersion,
      overrides: {},
    },
  } as Extract<HmiElement, { type: "symbol_instance" }>;
  return {
    ...document,
    elements: document.elements.map((entry) => (entry.id === elementId ? symbolElement : entry)),
    bindings: document.bindings.filter((binding) => binding.elementId !== elementId),
    symbols: nextSymbols,
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function upgradeSymbolInstances(
  document: HmiCanvasDocument,
  definitionId: string,
  mode: "selected" | "all",
  selectedElementId?: string | null,
): HmiCanvasDocument {
  const definition = document.symbols.find((symbol) => symbol.id === definitionId);
  if (!definition) {
    return document;
  }
  return {
    ...document,
    elements: document.elements.map((element) => {
      if (element.type !== "symbol_instance" || element.symbol.definitionId !== definitionId) {
        return element;
      }
      if (mode === "selected" && element.id !== selectedElementId) {
        return element;
      }
      if (element.symbol.definitionVersion === definition.version) {
        return element;
      }
      return {
        ...element,
        symbol: {
          ...element.symbol,
          definitionVersion: definition.version,
        },
      };
    }),
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}
